import { useState } from "react";

import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { useMutation, useQuery } from "@tanstack/react-query";
import { KeyIcon, Trash2Icon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { asMutation, initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import { UserChip } from "../../Chips";
import { UserChooser } from "../../Choosers";
import ConfigurableTable from "../../ConfigurableTable";
import RoleAssigner from "../../Roles/RoleAssigner";
import type { BusinessProfileRoleUser, IdeaBusinessProfile } from "../types";

type UsersProps = {
  businessProfile: IdeaBusinessProfile;
  onCloseModal: () => void;
};

export const Users = ({ businessProfile, onCloseModal }: UsersProps) => {
  const [assignRoleUser, setAssignRoleUser] =
    useState<BusinessProfileRoleUser | null>(null);
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const canManageUsers = util.hasPermission(
    user,
    "ideaBusinessProfile.editMembers",
    businessProfile._id,
  );

  const usersQuery = useQuery(
    initUseQuery(api.roles.getUsers, [
      "ideaBusinessProfile",
      businessProfile._id,
      {},
    ]),
  );

  const invitationQuery = useQuery(
    initUseQuery(
      api.invitations.getForType,
      ["ideaBusinessProfile", businessProfile._id],
      { isEnabled: canManageUsers },
    ),
  );

  const createInvitationMutation = useMutation({
    mutationKey: ["post", "invitations", "bulk"],
    mutationFn: asMutation(api.invitations.createBulk),
    onSuccess: () => {
      toast.success("Invitation sent");
      api.invalidateQueries(api.invitations.getForType, [
        "ideaBusinessProfile",
        businessProfile._id,
      ]);
    },
    onError: () => {
      toast.error("Failed to send invite");
    },
  });

  const removePendingInvitationMutation = useMutation({
    mutationKey: ["delete", "invitations"],
    mutationFn: asMutation(api.invitations.remove),
    onSuccess: () => {
      toast.success("Invitation removed. The user can no longer accept it.");
      api.invalidateQueries(api.invitations.getForType, [
        "ideaBusinessProfile",
        businessProfile._id,
      ]);
    },
    onError: () => {
      toast.error("Failed to remove invite");
    },
  });

  const removeUserFromRolesMutation = useMutation({
    mutationKey: [
      "delete",
      "roles",
      "ideaBusinessProfile",
      businessProfile._id,
      "users",
    ],
    mutationFn: asMutation(api.roles.removeUserFromAllRoles),
    onSuccess: () => {
      api.invalidateQueries(api.roles.getUsers, [
        "ideaBusinessProfile",
        businessProfile._id,
      ]);
    },
  });

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      {util.hasPermission(
        user,
        "ideaBusinessProfile.editMembers",
        businessProfile._id,
      ) ? (
        <>
          <RoleAssigner
            forType="ideaBusinessProfile"
            forId={businessProfile._id}
            forUsers={assignRoleUser ? [assignRoleUser._id] : []}
            permissionGroups={[
              {
                id: businessProfile._id,
                type: "ideaBusinessProfiles",
                title: "Idea Business Profile",
                permissions: constants.permissions.ideaBusinessProfile,
              },
            ]}
            onAssign={() => {
              api.invalidateQueries(api.roles.getUsers, [
                "ideaBusinessProfile",
                businessProfile._id,
                {},
              ]);
            }}
            onUnassign={() => {
              api.invalidateQueries(api.roles.getUsers, [
                "ideaBusinessProfile",
                businessProfile._id,
                {},
              ]);
            }}
            modalProps={{ onClose: () => setAssignRoleUser(null) }}
          />
          {(invitationQuery.data?.invitations?.length ?? 0) > 0 ? (
            <Card fluid style={{ alignSelf: "stretch" }}>
              <CardContent>
                <b style={{ fontSize: 18 }}>Pending invitations</b>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>User</TableHeaderCell>
                      <TableHeaderCell>Invited by</TableHeaderCell>
                      <TableHeaderCell>Created at</TableHeaderCell>
                      <TableHeaderCell></TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitationQuery.data?.invitations?.map((r) => (
                      <TableRow key={r.inviteeUser?._id || r.invitee}>
                        <TableCell>
                          {r.inviteeUser ? (
                            <UserChip user={r.inviteeUser} />
                          ) : (
                            r.invitee
                          )}
                        </TableCell>
                        <TableCell>
                          <UserChip user={r.inviterUser} />
                        </TableCell>
                        <TableCell>
                          {formatDate(r.createdAt, "dd/MM/yy")}
                        </TableCell>
                        <TableCell textAlign="right">
                          <Button
                            size="small"
                            onClick={() =>
                              removePendingInvitationMutation.mutate([r._id])
                            }
                            variant="outline"
                          >
                            <Icon icon={Trash2Icon} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}
      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "space-between",
          alignItems: "center",
          alignSelf: "stretch",
        }}
      >
        <b style={{ fontSize: 22 }}>Users</b>
        {util.hasPermission(
          user,
          "ideaBusinessProfile.editMembers",
          businessProfile._id,
        ) ? (
          <UserChooser
            trigger={<Button color="primary">Invite users</Button>}
            forType="ideaBusinessProfile"
            forId={businessProfile._id}
            enabledFeatures={{
              search: true,
              invite: true,
              persistentToken: true,
            }}
            searchFunction={(term, cb) =>
              api.roles.getPotentialRoleUsers(
                "ideaBusinessProfile",
                businessProfile._id,
                "default",
                { query: term },
                (res) => cb(res.users as OpenAPI.Schemas["User"][]),
                () => {},
              )
            }
            confirm="Invite users to profile"
            clearOnComplete
            onComplete={(users) => {
              const emailUsers = users.filter((u) => u.isEmailInvitee);
              if (emailUsers.length) {
                createInvitationMutation.mutate([
                  {
                    invitees: emailUsers.map((u) => u._id),
                    invitationType: "email",
                    forType: "ideaBusinessProfile",
                    forId: businessProfile._id,
                  },
                ]);
              }

              const internalInvitees = users.filter((u) => !u.isEmailInvitee);
              if (internalInvitees.length > 0) {
                createInvitationMutation.mutate([
                  {
                    invitees: internalInvitees.map((u) => u._id),
                    invitationType: "user",
                    forType: "ideaBusinessProfile",
                    forId: businessProfile._id,
                  },
                ]);
              }
            }}
          />
        ) : null}
      </div>
      <ConfigurableTable
        tableKey="businessProfileUsers"
        data={usersQuery.data?.users || []}
        keyExtractor={(user) => user._id}
        actions={(items) =>
          canManageUsers
            ? [
                {
                  name: "Assign roles",
                  icon: KeyIcon,
                  onClick: (items) => {
                    const selectedUser = items[0];
                    if (!selectedUser) {
                      return;
                    }
                    setAssignRoleUser(selectedUser);
                  },
                },
                {
                  name: "Remove from profile",
                  icon: Trash2Icon,
                  onClick: (items) => {
                    const selectedUser = items[0];
                    if (!selectedUser) {
                      return;
                    }
                    util
                      .confirm(
                        "Remove user",
                        "Are you sure you want to remove this user from the profile?",
                      )
                      .then(() => {
                        removeUserFromRolesMutation.mutate([
                          "ideaBusinessProfile",
                          businessProfile._id,
                          selectedUser._id,
                        ]);
                      })
                      .catch(() => {});
                  },
                },
              ]
            : items.length === 1 && user && items[0]?._id === user._id
              ? [
                  {
                    name: "Leave profile",
                    icon: Trash2Icon,
                    onClick: () => {
                      util
                        .confirm(
                          "Leave profile",
                          "Are you sure you want to leave this profile?",
                        )
                        .then(() => {
                          if (!user) {
                            return;
                          }
                          api.roles.removeUserFromAllRoles(
                            "ideaBusinessProfile",
                            businessProfile._id,
                            user._id,
                            () => {
                              dispatch(
                                actions.user.updatePermissions(
                                  businessProfile._id,
                                  [],
                                ),
                              );
                              onCloseModal();
                            },
                            () => {},
                          );
                        })
                        .catch(() => {});
                    },
                  },
                ]
              : []
        }
        columns={[
          {
            key: "image",
            name: "",
            settingName: "Image",
            width: 40,
            center: true,
            render: ({ item }) => (
              <Link to={`/users/${item._id}`}>
                <Image
                  style={{ objectFit: "cover" }}
                  avatar
                  src={util.avatarUrl(item)}
                />
              </Link>
            ),
          },
          {
            key: "firstName",
            name: "First name",
            render: ({ item }) => (
              <Link to={`/users/${item._id}`}>{item.profile.firstName}</Link>
            ),
          },
          {
            key: "lastName",
            name: "Last name",
            render: ({ item }) => (
              <Link to={`/users/${item._id}`}>{item.profile.lastName}</Link>
            ),
          },
          {
            key: "department",
            name: "Department",
            render: ({ item }) => item.profile.department,
          },
          {
            key: "roles",
            name: "Roles",
            render: ({ item }) =>
              (item.roles ?? []).map((r) => r.name).join(", "),
          },
          {
            key: "lastSeenAt",
            name: "Last seen",
            render: ({ item }) => formatDate(item.lastSeenAt, "dd/MM/yyyy"),
          },
        ]}
      />
    </div>
  );
};
